import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

/**
 * Trade Table Component
 *
 * 🔧 PRODUCTION VERSION (2025-12-29): Enhanced with useful trading information
 *
 * Features:
 * - P&L display with color coding
 * - Trade duration calculation
 * - Better currency formatting (INR/USDT)
 * - Expandable row details
 * - Quick action buttons
 */
const TradeTable = ({ trades, selectedTrades, onTradeSelect, onSelectAll, onBulkAction }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });
  const [expandedRow, setExpandedRow] = useState(null);

  const sortedTrades = useMemo(() => {
    if (!sortConfig?.key) return trades;

    return [...trades]?.sort((a, b) => {
      const aValue = a?.[sortConfig?.key];
      const bValue = b?.[sortConfig?.key];

      if (aValue < bValue) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [trades, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      executed: { color: 'bg-success text-success-foreground', icon: 'CheckCircle' },
      pending: { color: 'bg-warning text-warning-foreground', icon: 'Clock' },
      failed: { color: 'bg-error text-error-foreground', icon: 'XCircle' },
      cancelled: { color: 'bg-secondary text-secondary-foreground', icon: 'MinusCircle' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;

    return (
      <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        <Icon name={config?.icon} size={12} />
        <span className="capitalize">{status}</span>
      </div>
    );
  };

  const getActionBadge = (action) => {
    const actionConfig = {
      buy: { color: 'text-success', icon: 'TrendingUp' },
      sell: { color: 'text-error', icon: 'TrendingDown' }
    };

    const config = actionConfig?.[action] || actionConfig?.buy;

    return (
      <div className={`inline-flex items-center space-x-1 ${config?.color}`}>
        <Icon name={config?.icon} size={14} />
        <span className="font-medium uppercase">{action}</span>
      </div>
    );
  };

  const formatCurrency = (amount, currencyType = 'INR') => {
    if (currencyType === 'USDT' || currencyType === 'USD') {
      return `$${amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
    }
    return `₹${amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  const formatCompactCurrency = (amount, currencyType = 'INR') => {
    const symbol = currencyType === 'USDT' || currencyType === 'USD' ? '$' : '₹';
    if (Math.abs(amount) >= 10000000) return `${symbol}${(amount / 10000000).toFixed(2)}Cr`;
    if (Math.abs(amount) >= 100000) return `${symbol}${(amount / 100000).toFixed(2)}L`;
    if (Math.abs(amount) >= 1000) return `${symbol}${(amount / 1000).toFixed(1)}K`;
    return `${symbol}${amount?.toFixed(2)}`;
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp)?.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp)?.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short'
    });
  };

  const calculateDuration = (entryTime, exitTime) => {
    if (!entryTime || !exitTime) return null;
    const entry = new Date(entryTime);
    const exit = new Date(exitTime);
    const diffMs = exit - entry;

    if (diffMs < 0) return null;
    if (diffMs < 1000) return `${diffMs}ms`;
    if (diffMs < 60000) return `${(diffMs / 1000).toFixed(1)}s`;
    if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m`;
    if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h ${Math.floor((diffMs % 3600000) / 60000)}m`;
    return `${Math.floor(diffMs / 86400000)}d`;
  };

  const getCurrencyType = (trade) => {
    if (trade.currency) return trade.currency;
    if (trade.symbol?.includes('USDT')) return 'USDT';
    return 'INR';
  };

  const getLatencyColor = (latency) => {
    if (latency < 100) return 'text-success';
    if (latency < 500) return 'text-warning';
    return 'text-error';
  };

  const getPnLColor = (pnl) => {
    if (pnl > 0) return 'text-success';
    if (pnl < 0) return 'text-error';
    return 'text-muted-foreground';
  };

  // Enhanced columns with more useful info
  const columns = [
    { key: 'timestamp', label: 'Date/Time', sortable: true, width: 'w-28' },
    { key: 'symbol', label: 'Asset', sortable: true, width: 'w-32' },
    { key: 'action', label: 'Side', sortable: true, width: 'w-16' },
    { key: 'quantity', label: 'Qty', sortable: true, width: 'w-20' },
    { key: 'price', label: 'Entry Price', sortable: true, width: 'w-28' },
    { key: 'amount', label: 'Value', sortable: true, width: 'w-28' },
    { key: 'pnl', label: 'P&L', sortable: true, width: 'w-28' },
    { key: 'status', label: 'Status', sortable: true, width: 'w-24' },
    { key: 'aiConfidence', label: 'AI Score', sortable: true, width: 'w-24' },
    { key: 'duration', label: 'Duration', sortable: false, width: 'w-20' },
    { key: 'actions', label: '', sortable: false, width: 'w-16' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Table Header */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border sticky top-0 z-10">
            <tr>
              <th className="w-12 px-4 py-3">
                <Checkbox
                  checked={selectedTrades?.length === trades?.length && trades?.length > 0}
                  onChange={(e) => onSelectAll(e?.target?.checked)}
                  size="sm"
                />
              </th>
              {columns?.map((column) => (
                <th
                  key={column?.key}
                  className={`${column?.width} px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider`}
                >
                  {column?.sortable ? (
                    <button
                      onClick={() => handleSort(column?.key)}
                      className="flex items-center space-x-1 hover:text-foreground transition-smooth"
                    >
                      <span>{column?.label}</span>
                      <Icon
                        name={
                          sortConfig?.key === column?.key
                            ? sortConfig?.direction === 'asc' ?'ChevronUp' :'ChevronDown' :'ChevronsUpDown'
                        }
                        size={12}
                      />
                    </button>
                  ) : (
                    <span>{column?.label}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedTrades?.map((trade) => (
              <tr
                key={trade?.id}
                className={`hover:bg-muted/50 transition-smooth ${
                  selectedTrades?.includes(trade?.id) ? 'bg-accent/10' : ''
                } ${trade?.isNew ? 'animate-pulse-subtle bg-accent/5' : ''}`}
              >
                <td className="px-4 py-3">
                  <Checkbox
                    checked={selectedTrades?.includes(trade?.id)}
                    onChange={(e) => onTradeSelect(trade?.id, e?.target?.checked)}
                    size="sm"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col">
                    <span className="text-sm font-mono text-foreground">{formatTime(trade?.timestamp)}</span>
                    <span className="text-xs text-muted-foreground">{formatDate(trade?.timestamp)}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">{trade?.symbol}</span>
                    <span className="text-xs text-muted-foreground">{trade?.assetType}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {getActionBadge(trade?.action)}
                </td>
                <td className="px-4 py-3 text-sm font-medium text-foreground">
                  {trade?.quantity?.toLocaleString('en-IN')}
                </td>
                <td className="px-4 py-3 text-sm font-mono text-foreground">
                  {formatCurrency(trade?.price, getCurrencyType(trade))}
                </td>
                <td className="px-4 py-3 text-sm font-mono text-foreground">
                  {formatCompactCurrency(trade?.amount, getCurrencyType(trade))}
                </td>
                <td className="px-4 py-3">
                  {getStatusBadge(trade?.status)}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-16 bg-muted rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          trade?.aiConfidence >= 80 ? 'bg-success' :
                          trade?.aiConfidence >= 60 ? 'bg-warning' : 'bg-error'
                        }`}
                        style={{ width: `${trade?.aiConfidence}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-foreground">
                      {trade?.aiConfidence}%
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {trade?.pnl !== undefined && trade?.pnl !== null ? (
                    <div className={`text-sm font-semibold ${getPnLColor(trade.pnl)}`}>
                      {trade.pnl >= 0 ? '+' : ''}{formatCurrency(trade.pnl, getCurrencyType(trade))}
                      {trade.pnlPercent && (
                        <span className="text-xs ml-1 opacity-75">
                          ({trade.pnlPercent >= 0 ? '+' : ''}{trade.pnlPercent.toFixed(2)}%)
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {calculateDuration(trade?.entryTime || trade?.timestamp, trade?.exitTime) ? (
                    <span className="text-sm text-muted-foreground font-mono">
                      {calculateDuration(trade?.entryTime || trade?.timestamp, trade?.exitTime)}
                    </span>
                  ) : trade?.status === 'executed' ? (
                    <span className="text-xs text-success">Closed</span>
                  ) : (
                    <span className="text-xs text-warning animate-pulse">Open</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="MoreHorizontal"
                    iconSize={16}
                    className="text-muted-foreground hover:text-foreground"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Empty State */}
      {trades?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12">
          <Icon name="Database" size={48} className="text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No trades found</h3>
          <p className="text-sm text-muted-foreground">
            Try adjusting your filters or check back later for new trades.
          </p>
        </div>
      )}
    </div>
  );
};

export default TradeTable;